import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BottomSheet } from "./BottomSheet";
import { useActiveChannel } from "../hooks/channels";
import { useAppStore } from "../store/app";
import { api } from "../lib/api";
import { haptics } from "../lib/telegram";

interface CrawlJob {
  job_id: string;
  status: string;
  indexed: number;
  total: number | null;
}

/**
 * The admin hub — a LAUNCHER, not a workspace (the user's chosen model). One tap
 * from the header shield, shown only when the active channel is one you admin.
 * Phase 2 wires the two ready actions (index status + re-crawl); the heavier rows
 * are stubs that will open full-screen /admin/* routes in Phase 4.
 */
export function AdminHub() {
  const open = useAppStore((s) => s.adminHubOpen);
  const setOpen = useAppStore((s) => s.setAdminHubOpen);
  const { channel } = useActiveChannel();
  const qc = useQueryClient();

  const channelId = channel?.id ?? null;
  const isAdmin = !!channel?.is_admin;

  const status = useQuery<{ job: CrawlJob | null }>({
    queryKey: ["crawl-status", channelId],
    enabled: open && isAdmin && !!channelId,
    refetchInterval: (q) => {
      const s = (q.state.data as { job: CrawlJob | null } | undefined)?.job?.status;
      return s === "running" || s === "pending" ? 3000 : false;
    },
    queryFn: () => api<{ job: CrawlJob | null }>(`/channels/${channelId}/crawl`),
  });

  const recrawl = useMutation({
    mutationFn: () => api<{ job_id: string; status: string }>(`/channels/${channelId}/crawl`, { method: "POST" }),
    onSuccess: () => {
      haptics.notify("success");
      qc.invalidateQueries({ queryKey: ["crawl-status", channelId] });
    },
    onError: () => haptics.notify("error"),
  });

  // Guard: never render admin controls for a non-admin channel.
  if (!isAdmin || !channel) return <BottomSheet open={open} onClose={() => setOpen(false)} />;

  const job = status.data?.job ?? null;
  const pct = job?.total ? Math.min(100, Math.round((job.indexed / job.total) * 100)) : null;

  return (
    <BottomSheet open={open} onClose={() => setOpen(false)} title="Admin panel">
      <div className="space-y-4 pb-2">
        {/* Index status — real */}
        <section className="rounded-xl bg-tg-bg-section p-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-tg-text">Indexing</h3>
            <span className="text-xs text-tg-hint">{job ? job.status : "not started"}</span>
          </div>
          {job && (
            <p className="mt-1 text-sm text-tg-subtitle">
              {job.indexed.toLocaleString()}
              {job.total ? ` of ${job.total.toLocaleString()}` : ""} indexed
              {pct != null ? ` · ${pct}%` : ""}
            </p>
          )}
          <button
            type="button"
            disabled={recrawl.isPending}
            onClick={() => recrawl.mutate()}
            className="mt-2 w-full rounded-lg bg-tg-button py-2 text-sm font-medium text-tg-button-text active:scale-[0.98] disabled:opacity-60"
          >
            {recrawl.isPending ? "Starting…" : job ? "Re-crawl channel" : "Start crawl"}
          </button>
        </section>

        {/* Permission flags — read-only mirror of Telegram settings */}
        <section className="rounded-xl bg-tg-bg-section p-3 text-sm">
          <h3 className="mb-1 font-semibold text-tg-text">Content protection</h3>
          <p className="text-tg-subtitle">Downloads: {channel.allow_downloads ? "allowed" : "restricted"}</p>
          <p className="text-tg-subtitle">Forwarding: {channel.forward_restricted ? "restricted" : "allowed"}</p>
        </section>

        {/* Phase 4 launchers — stubbed so the launcher→full-screen pattern exists */}
        <section className="rounded-xl bg-tg-bg-section">
          {[
            { icon: "🏷", label: "Bulk tagging" },
            { icon: "🗂", label: "Manage collections" },
            { icon: "🚩", label: "Moderation queue" },
            { icon: "📊", label: "Analytics" },
            { icon: "🔒", label: "Gating & pricing" },
          ].map((row, i) => (
            <button
              key={row.label}
              type="button"
              onClick={() => haptics.impact("light")}
              className={
                "flex w-full items-center gap-3 px-3 py-3 text-left text-sm active:bg-tg-bg-secondary " +
                (i > 0 ? "border-t border-tg-separator" : "")
              }
            >
              <span>{row.icon}</span>
              <span className="flex-1 text-tg-text">{row.label}</span>
              <span className="rounded-md bg-tg-bg-secondary px-1.5 py-0.5 text-[10px] text-tg-hint">Soon</span>
            </button>
          ))}
        </section>
      </div>
    </BottomSheet>
  );
}
